import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useWebPhoneV4, WebPhoneCredentials, ConnectionStatus, CallStatus, CallSession, RTCStats } from '../hooks/useWebPhoneV4';
import api from '../services/api';

interface WebPhoneContextV4Type {
  isWindowOpen: boolean;
  isMinimized: boolean;
  connectionStatus: ConnectionStatus;
  callStatus: CallStatus;
  currentCall: CallSession | null;
  consultCall: CallSession | null;
  openWindow: () => void;
  closeWindow: () => void;
  minimizeWindow: () => void;
  restoreWindow: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
  openPictureInPicture: () => Promise<void>;
  // Forward all webphone functions
  makeCall: (number: string) => void;
  answerCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  toggleHold: () => void;
  transferCall: (target: string) => void;
  startConsultCall: (target: string) => void;
  cancelConsultCall: () => void;
  completeAttendedTransfer: () => void;
  isMuted: boolean;
  isOnHold: boolean;
  dialedNumber: string;
  addDigit: (digit: string) => void;
  deleteDigit: () => void; // NEW: Delete last digit
  clearNumber: () => void;
  errorMessage: string | null;
  // Audio device management
  audioDevices: { inputDevices: MediaDeviceInfo[], outputDevices: MediaDeviceInfo[] };
  selectedSpeaker: string;
  selectedMicrophone: string;
  volume: number;
  changeSpeaker: (deviceId: string) => void;
  changeMicrophone: (deviceId: string) => void;
  changeVolume: (volume: number) => void;
  enumerateDevices: () => Promise<void>;
  testSpeaker: () => Promise<void>;
  // RTC Statistics
  rtcStats: RTCStats | null;
  // V4: Event Logs removed - no data collection
  // Debug Mode
  debugMode: boolean;
  toggleDebugMode: () => void;
}

const WebPhoneContextV4 = createContext<WebPhoneContextV4Type | undefined>(undefined);

export const useWebPhoneContextV4 = () => {
  const context = useContext(WebPhoneContextV4);
  if (!context) {
    throw new Error('useWebPhoneContextV4 must be used within WebPhoneProviderV4');
  }
  return context;
};

export const WebPhoneProviderV4: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isWindowOpen, setIsWindowOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [credentials, setCredentials] = useState<WebPhoneCredentials | null>(null);
  const hasAutoConnectedRef = useRef(false);

  // Debug logging
  useEffect(() => {
    console.log('[WebPhoneContextV4] State changed - isWindowOpen:', isWindowOpen, 'isMinimized:', isMinimized);
  }, [isWindowOpen, isMinimized]);

  const webphone = useWebPhoneV4();

  // Fetch credentials from API
  const fetchCredentials = useCallback(async () => {
    try {
      console.log('[WebPhoneV4] Fetching credentials from API...');
      console.log('[WebPhoneV4] API endpoint: /endpoints/webphone-credentials');

      // Check if we have an auth token before making the request
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      console.log('[WebPhoneV4] Auth token present:', !!token);
      if (token) {
        console.log('[WebPhoneV4] Token preview:', token.substring(0, 30) + '...');
      } else {
        console.error('[WebPhoneV4] NO TOKEN FOUND - API call will likely fail or hang');
      }

      console.log('[WebPhoneV4] About to call api.get...');
      const response = await api.get('/endpoints/webphone-credentials');
      console.log('[WebPhoneV4] API call returned, checking response...');
      console.log('[WebPhoneV4] Response status:', response.status);
      console.log('[WebPhoneV4] Response data:', response.data);
      if (response.data.success) {
        console.log('[WebPhoneV4] Success=true, extracting credentials...');
        setCredentials(response.data.data);
        console.log('[WebPhoneV4] Credentials fetched successfully:', response.data.data);
        return response.data.data;
      }
      console.error('[WebPhoneV4] Success=false, throwing error');
      throw new Error(response.data.message || 'Failed to fetch credentials');
    } catch (error: any) {
      console.error('[WebPhoneV4] CATCH BLOCK - Failed to fetch credentials:', error);
      console.error('[WebPhoneV4] Error name:', error.name);
      console.error('[WebPhoneV4] Error message:', error.message);
      console.error('[WebPhoneV4] Error response:', error.response);
      console.error('[WebPhoneV4] Error details:', error.response?.data || error.message);
      throw error;
    }
  }, []);

  // Connect to WebRTC
  const connect = useCallback(async () => {
    try {
      console.log('[WebPhoneV4] Connect called, current status:', webphone.connectionStatus);
      let creds = credentials;
      if (!creds) {
        console.log('[WebPhoneV4] No cached credentials, fetching from API...');
        creds = await fetchCredentials();
        console.log('[WebPhoneV4] fetchCredentials() returned:', creds ? 'GOT CREDENTIALS' : 'NULL/UNDEFINED');
      } else {
        console.log('[WebPhoneV4] Using cached credentials');
      }
      if (creds) {
        console.log('[WebPhoneV4] Connecting to SBC with credentials:', { username: creds.username, sbcServers: creds.sbcServers });
        await webphone.connect(creds);
        console.log('[WebPhoneV4] webphone.connect() completed');
      } else {
        console.error('[WebPhoneV4] No credentials available - creds is null/undefined');
      }
    } catch (error) {
      console.error('[WebPhoneV4] CONNECT CATCH BLOCK - Connect failed:', error);
      console.error('[WebPhoneV4] Error details:', error);
    }
  }, [credentials, fetchCredentials, webphone]);

  // Connect when window opens
  useEffect(() => {
    if (isWindowOpen && webphone.connectionStatus === 'disconnected' && !hasAutoConnectedRef.current) {
      console.log('[WebPhoneV4] Window opened - connecting...');
      hasAutoConnectedRef.current = true;
      connect();
    } else if (!isWindowOpen) {
      // Reset the flag when window closes so next open will connect
      hasAutoConnectedRef.current = false;
    }
  }, [isWindowOpen, webphone.connectionStatus, connect]);

  const openWindow = useCallback(() => {
    console.log('[WebPhoneContextV4] openWindow called');
    setIsWindowOpen(true);
    setIsMinimized(false);
  }, []);

  const closeWindow = useCallback(() => {
    console.log('[WebPhoneContextV4] closeWindow called');
    setIsWindowOpen(false);
    setIsMinimized(false);
    webphone.disconnect();
  }, [webphone]);

  const minimizeWindow = useCallback(() => {
    console.log('[WebPhoneContextV4] minimizeWindow called');
    setIsMinimized(true);
  }, []);

  const restoreWindow = useCallback(() => {
    console.log('[WebPhoneContextV4] restoreWindow called');
    setIsMinimized(false);
  }, []);

  const openPictureInPicture = useCallback(async () => {
    try {
      const width = 340;
      const height = 600;
      const left = window.screen.width - width - 20;
      const top = 80;

      const popup = window.open(
        '/webphone-popup-v4',
        'WebPhoneV4',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,menubar=no,toolbar=no,location=no`
      );

      if (popup) {
        console.log('[WebPhoneV4] Popup window opened');
      } else {
        alert('Popup blocked. Please allow popups for this site.');
      }
    } catch (error) {
      console.error('[WebPhoneV4] Failed to open popup:', error);
      alert('Failed to open WebPhone window.');
    }
  }, []);

  const value: WebPhoneContextV4Type = {
    isWindowOpen,
    isMinimized,
    connectionStatus: webphone.connectionStatus,
    callStatus: webphone.callStatus,
    currentCall: webphone.currentCall,
    consultCall: webphone.consultCall,
    openWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    connect,
    disconnect: webphone.disconnect,
    openPictureInPicture,
    makeCall: webphone.makeCall,
    answerCall: webphone.answerCall,
    hangUp: webphone.hangUp,
    toggleMute: webphone.toggleMute,
    toggleHold: webphone.toggleHold,
    transferCall: webphone.transferCall,
    startConsultCall: webphone.startConsultCall,
    cancelConsultCall: webphone.cancelConsultCall,
    completeAttendedTransfer: webphone.completeAttendedTransfer,
    isMuted: webphone.isMuted,
    isOnHold: webphone.isOnHold,
    dialedNumber: webphone.dialedNumber,
    addDigit: webphone.addDigit,
    deleteDigit: webphone.deleteDigit, // NEW
    clearNumber: webphone.clearNumber,
    errorMessage: webphone.errorMessage,
    audioDevices: webphone.audioDevices,
    selectedSpeaker: webphone.selectedSpeaker,
    selectedMicrophone: webphone.selectedMicrophone,
    volume: webphone.volume,
    changeSpeaker: webphone.changeSpeaker,
    changeMicrophone: webphone.changeMicrophone,
    changeVolume: webphone.changeVolume,
    enumerateDevices: webphone.enumerateDevices,
    testSpeaker: webphone.testSpeaker,
    rtcStats: webphone.rtcStats, // V4: Always null - stats disabled
    // V4: eventLogs removed - no data collection
    debugMode: webphone.debugMode,
    toggleDebugMode: webphone.toggleDebugMode,
  };

  return <WebPhoneContextV4.Provider value={value}>{children}</WebPhoneContextV4.Provider>;
};
